import { useState, useEffect, forwardRef, useImperativeHandle, ReactNode } from 'react';
import styled from 'styled-components';

interface Props {
  top?: ReactNode;
  center?: ReactNode;
  bottom?: ReactNode;
  onClose?: () => void;
  disableContainerToggle?: boolean;
}

const Component = forwardRef((props: Props, ref) => {
  const [open, setOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const toggle = (shouldOpen = true) => {
    if (open) {
      setIsClosing(true);
      if (props?.onClose) {
        props?.onClose();
      }
    } else if (shouldOpen) {
      setOpen(true);
    }
  };

  useEffect(() => {
    if (isClosing) {
      const timer = setTimeout(() => {
        setOpen(false);
        setIsClosing(false);
      }, 500); // match this with the duration of your animation

      return () => clearTimeout(timer);
    }
  }, [isClosing]);

  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  useImperativeHandle(ref, () => ({
    toggle,
    setOpen,
    open,
  }));

  return (
    <>
      {(open || isClosing) && (
        <Container
          $isClosing={isClosing}
          onClick={() => {
            if (props.disableContainerToggle) {
              return;
            }
            toggle();
          }}
        >
          <Modal $isClosing={isClosing} onClick={stopPropagation}>
            <div>
              {props.top || (
                <TopDefault>
                  <svg
                    onClick={() => toggle(false)}
                    xmlns="http://www.w3.org/2000/svg"
                    height="48"
                    width="48"
                  >
                    <path d="m12.45 37.65-2.1-2.1L21.9 24 10.35 12.45l2.1-2.1L24 21.9l11.55-11.55 2.1 2.1L26.1 24l11.55 11.55-2.1 2.1L24 26.1Z" />
                  </svg>
                </TopDefault>
              )}
            </div>
            <div className="center">
              {props.center || <CenterDefault>Content example... (scroll down)</CenterDefault>}
            </div>
            <div>{props.bottom || <BottomDefault>Bottom example...</BottomDefault>}</div>
          </Modal>
        </Container>
      )}
    </>
  );
});

export default Component;

/*** STYLING ***/
export const Container = styled.div<{ $isClosing: boolean }>`
  display: flex;
  position: fixed;
  background-color: rgba(0, 0, 0, 0.07);
  z-index: 1000;
  justify-content: right;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100svh;
  height: 100lvh;
  height: 100dvh;
  animation: ${({ $isClosing }) => ($isClosing ? 'fadeOut 0.5s forwards' : 'fadeIn 1s forwards')};

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  @keyframes fadeOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
`;
const Modal = styled.div<{ $isClosing: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: max-content 1fr max-content;
  grid-gap: 0px;
  width: 100%;
  max-width: 408px;
  background-color: white;
  border-radius: 0px;
  box-shadow: -16px 0px 107px 4px rgba(0, 0, 0, 0.05);
  height: 100vh;
  height: 100svh;
  height: 100lvh;
  height: 100dvh;
  position: relative;
  overflow: auto;
  animation: ${({ $isClosing }) =>
    $isClosing ? 'toRight 0.5s forwards' : 'fromRight 1s forwards'};
  > {
    box-sizing: border-box;
  }

  @keyframes fromRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes toRight {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(100%);
    }
  }
`;

const TopDefault = styled.div`
  display: flex;
  justify-content: right;
  width: 100%;
  padding: 32px;
  box-shadow: 0 15px 10px -12px rgb(0 0 0 / 7%);

  svg {
    scale: 0.7;
    fill: rgba(0, 0, 0, 0.65);
    transition: 0.3s;
    cursor: pointer;
  }
  svg:hover {
    fill: black;
  }
`;

const CenterDefault = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 32px 64px;
  color: #555;
  height: calc(100% + 200px);
  font-family: sans-serif;
  font-weight: bold;
`;

const BottomDefault = styled.div`
  display: flex;
  justify-content: center;
  background-color: #f1f1f1;
  width: 100%;
  padding: 32px;
  color: #555;
  font-family: sans-serif;
  font-weight: bold;
`;
